import type { FieldError } from 'react-hook-form';
import type { LimitRetValue } from '../components/controlled-input-field/util';
import type { ReviewErrorTranslationKeys } from '../review-configuration/ReviewConfiguration';
import type { LotteryFormAPIErrorType } from '../types';

export const buildApiError = (
  isAPIError: boolean,
  reviewErrorTranslationKeys: ReviewErrorTranslationKeys,
  resetAPIError: () => void
): LotteryFormAPIErrorType | undefined => {
  if (isAPIError) {
    return {
      reviewErrorTranslationKeys,
      resetAPIError,
    };
  }
  return undefined;
};

type ErrorType = {
  message: string;
  type: string;
};

export type UploadErrorsType = {
  question: ErrorType;
  description: ErrorType;
  correctAnswerText: ErrorType;
  wrongAnswerText: ErrorType;
  image: ErrorType;
};

export type QuestionsErrorFormatLimitsType = {
  questionMinLength: number | undefined;
  questionMaxLength: number | undefined;
  descriptionMinLength: number | undefined;
  descriptionMaxLength: number | undefined;
  correctAnswerTextMinLength: number | undefined;
  correctAnswerTextMaxLength: number | undefined;
  wrongAnswerTextMinLength: number | undefined;
  wrongAnswerTextMaxLength: number | undefined;
  imageMaxLength: number | undefined;
  maxNumberItems: number;
};

/**
 * Get rows with errors
 *
 * Get rows with errors updated with offset to compensate for column name and start row
 *
 * @param error origin error
 * @returns the array of errors with updated row index
 */
export const getRowsWithErrors = (error: FieldError | undefined): Array<UploadErrorsType> => {
  // row offset to compensate for column name and start row
  const rowOffsetInFile = 2;
  const rowsWithError: Array<UploadErrorsType> = [];

  if (Array.isArray(error)) {
    error.forEach((_error, idx) => {
      if (Object.keys(_error).length > 0) {
        rowsWithError[idx + rowOffsetInFile] = _error;
      }
    });
  }
  return rowsWithError;
};

export const getLimit = (
  errorMessage: string,
  questionsErrorFormatLimit: QuestionsErrorFormatLimitsType
) => {
  let limit: LimitRetValue = { limit: 0 };
  switch (errorMessage) {
    case 'swipe-upload-questions-question-error-message-min-length':
      limit = { limit: questionsErrorFormatLimit.questionMinLength };
      break;
    case 'swipe-upload-questions-question-error-message-max-length':
      limit = { limit: questionsErrorFormatLimit.questionMaxLength };
      break;
    case 'swipe-upload-questions-description-error-message-min-length':
      limit = { limit: questionsErrorFormatLimit.descriptionMinLength };
      break;
    case 'swipe-upload-questions-description-error-message-max-length':
      limit = { limit: questionsErrorFormatLimit.descriptionMaxLength };
      break;
    case 'swipe-upload-questions-image-error-message-max-length':
      limit = { limit: questionsErrorFormatLimit.imageMaxLength };
      break;
    case 'swipe-upload-questions-error-message-items-max-number':
      limit = { limit: questionsErrorFormatLimit.maxNumberItems };
      break;
    case 'swipe-upload-questions-correct-answer-text-error-message-min-length':
      limit = { limit: questionsErrorFormatLimit.correctAnswerTextMinLength };
      break;
    case 'swipe-upload-questions-correct-answer-text-error-message-max-length':
      limit = { limit: questionsErrorFormatLimit.correctAnswerTextMaxLength };
      break;
    case 'swipe-upload-questions-wrong-answer-text-error-message-min-length':
      limit = { limit: questionsErrorFormatLimit.wrongAnswerTextMinLength };
      break;
    case 'swipe-upload-questions-wrong-answer-text-error-message-max-length':
      limit = { limit: questionsErrorFormatLimit.wrongAnswerTextMaxLength };
      break;

    default:
      break;
  }
  return limit;
};
